import {
  CompletionTargetUnit,
  goalInboxID,
  goalOtherID,
  Habit,
  HabitSchedule,
  IconFormNames,
  ID,
  lifeAreaOtherID,
  NewHabit,
  NewTask,
  ScheduleEntry,
  ScheduleEntryState,
  Task,
  TaskType,
} from '@griply/types';
import { endOfDay, startOfDay } from 'date-fns';

import { FirestoreDataConverter } from '../types/firestore';
import {
  firebaseObjectTimestampToDate,
  firebaseTimestampToDate,
} from './firebase-timestamp-to-date';

export const taskConverter: FirestoreDataConverter<Task> = {
  fromFirestore: (snapshot) => {
    const rawData = firebaseObjectTimestampToDate(snapshot.data());
    const data = rawData as Record<string, unknown>;
    data.id = snapshot.id;

    let parsedData: Partial<Task> = {
      type: TaskType.Task,
      createdAt: (data.createdAt as Date | undefined) ?? new Date(),
      updatedAt: (data.updatedAt as Date | undefined) ?? new Date(),
      startDate:
        ((data.startDate as Date | undefined)
          ? (data.startDate as Date)
          : (data.createdAt as Date | undefined)) ?? new Date(),
      reminderTime: (data.reminderTime as number | null | undefined) ?? null,
      iconName: (data.iconName as IconFormNames | null | undefined) ?? null,
      parentIds: (data.parentIds as ID[])?.length
        ? (data.parentIds as ID[])
        : null,
      schedules: null,
    };

    if (
      [TaskType.Habit, TaskType.Repeating].includes(data.type as TaskType) &&
      (data.schedules as HabitSchedule[] | undefined)?.length
    ) {
      parsedData = parsedData as unknown as Habit;
      parsedData.type = data.type as TaskType.Habit;
      parsedData.iconName =
        (data.iconName as IconFormNames | undefined) ?? IconFormNames.Repeat;
      parsedData.schedules = (
        data.schedules as HabitSchedule[]
      ).map<HabitSchedule>((schedule) => {
        // Build a Set of day-timestamps from existing entries for O(1) lookup
        // instead of O(n×m) isSameDay scans over the entries array.
        const existingEntryDays = new Set(
          (schedule.entries ?? []).map((entry) =>
            startOfDay(entry.date).getTime(),
          ),
        );

        return {
          ...schedule,
          startDate: schedule.startDate ?? new Date(),
          endDate: firebaseTimestampToDate(schedule.endDate),
          entries: (
            [
              ...(schedule.entries ?? []),
              ...(schedule.completions?.map((date) =>
                existingEntryDays.has(startOfDay(date).getTime())
                  ? undefined
                  : {
                      id: `${date}`,
                      date: date,
                      state: ScheduleEntryState.Complete,
                      value: 1,
                    },
              ) ?? []),
              ...(schedule.skips?.map((date) =>
                existingEntryDays.has(startOfDay(date).getTime())
                  ? undefined
                  : {
                      id: `${date}`,
                      date: date,
                      state: ScheduleEntryState.Skip,
                      value: 1,
                    },
              ) ?? []),
            ] as ScheduleEntry[]
          )
            .filter(Boolean)
            .sort(
              (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
            ),
          plannedOccurrences: uniqBy(
            schedule.plannedOccurrences ?? [],
            // Deduplicate by day using integer truncation — avoids a Date
            // allocation per occurrence that startOfDay() would incur.
            ({ date }) =>
              date ? Math.floor(date.getTime() / 86_400_000) : null,
          ),
          completionTarget: {
            // @ts-expect-error -- spread overwriting is explicitly meant like this
            count: 1,
            // @ts-expect-error -- spread overwriting is explicitly meant like this
            unit: CompletionTargetUnit.Day,
            ...(schedule.completionTarget ?? {}),
          },
        };
      });
    }

    return {
      ...data,
      ...parsedData,
    } as Task;
  },
  toFirestore: (task) => task,
};

const excludedGoalIds = [goalInboxID, goalOtherID];

export const newTaskConverter = (task: NewTask) => {
  const deadline = task.endStrategy?.deadline;

  return {
    ...task,
    type: task.schedules?.length
      ? [TaskType.Repeating, TaskType.Habit].includes(task.type)
        ? task.type
        : TaskType.Repeating
      : TaskType.Task,
    iconName: (task as NewHabit).iconName ?? null,
    createdAt: new Date(),
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    duration: null,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    goalId: excludedGoalIds.includes(task.goalId!) ? null : task.goalId ?? null,
    lifeAreaId:
      task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId ?? null,
    reminderTime: task.reminderTime ?? null,
    priority: task.priority ?? null,
    endStrategy: {
      deadline: deadline ? endOfDay(deadline) : null,
      completionCount: task.endStrategy?.completionCount ?? null,
    },
    schedules: task.schedules?.length
      ? task.schedules.map((schedule) => ({
          ...schedule,
          startDate: schedule.startDate
            ? new Date(schedule.startDate)
            : new Date(),
          endDate: schedule.endDate ? new Date(schedule.endDate) : null,
          entries: schedule.entries?.map((entry) => ({
            ...entry,
            date: entry.date ? new Date(entry.date) : null,
          })),
        }))
      : null,
  };
};

// Helper function to replace lodash/uniqBy
const uniqBy = <T>(array: T[], iteratee: (item: T) => unknown): T[] => {
  const seen = new Set();
  return array.filter((item) => {
    const key = iteratee(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};
